import React, { useState, useEffect, RefObject } from 'react';
import AlertsComponent from 'components/Alert/AlertsComponent';
import Button from 'components/Button/Button';
import debounce from '@sd-utility/debounce';
import { useExperimentsContext } from 'context/ExperimentsContext';
import StarRating from 'components/RatingsAndReviews/StarRating';
import Price from 'components/Price';
import { InsuranceOffer, ProductPriceAmount } from 'types/Product';
import DynamicPrice from 'components/DynamicPrice';
import StockMessageDisplay from 'components/StockMessage/StockMessageDisplay';
import { useSelectedSkuContext } from 'context/SelectedSku/SelectedSkuContext';
import styles from './StickyAddToBasketButton.module.css';

const lessThan100PxFromBottomOfPage = () => window.innerHeight + window.pageYOffset >= document.body.offsetHeight - 100;

interface Props {
  primaryAddToBasketButtonRef: RefObject<HTMLDivElement>;
  handleAddToBasket: (buttonLabel?: string, insuranceOverride?: InsuranceOffer) => void;
  disabled: boolean;
  updateBasket?: boolean;
  errorMessage?: string;
  productData: {
    price: ProductPriceAmount;
    reviewTotal?: number;
    reviewStars?: number;
  };
}

function StickyAddToBasketButton({
  primaryAddToBasketButtonRef,
  handleAddToBasket,
  disabled,
  updateBasket = false,
  errorMessage,
  productData,
}: Props) {
  const [containerIsVisible, setContainerIsVisible] = useState(false);
  const experiments = useExperimentsContext();
  const challengerEnabled = experiments['se-8'];
  const { selectedSku, selectedInsurance, isAddOnSelected } = useSelectedSkuContext();

  const renderPrice = () => {
    const shouldShowDynamicPrice =
      (selectedSku && productData.price?.currentFrom) ||
      (selectedSku && ((selectedInsurance && typeof selectedInsurance !== 'string') || isAddOnSelected));

    return (
      <div className={styles.priceContainer} data-testid="sticky_price_container">
        {shouldShowDynamicPrice ? (
          <DynamicPrice price={productData.price} force />
        ) : (
          <Price price={productData.price} />
        )}
      </div>
    );
  };

  const renderStarRating = () => {
    if (!productData?.reviewTotal || productData?.reviewTotal <= 0 || !productData?.reviewStars) {
      return null;
    }

    return (
      <div className={styles.starRatingContainer} data-testid="sticky_star_rating_container">
        <StarRating reviewTotal={productData.reviewTotal} reviewStars={productData.reviewStars} size="medium" />
      </div>
    );
  };

  const renderStockMessage = () => (
    <div className={styles.stockMessageContainer} data-testid="sticky_stock_message_container">
      <StockMessageDisplay />
    </div>
  );

  const renderProductInfo = () => {
    if (!challengerEnabled) return null;

    return (
      <>
        <div className={styles.productInfoRow}>
          {renderPrice()}
          {renderStarRating()}
        </div>

        {renderStockMessage()}
      </>
    );
  };

  const renderErrorMessage = () => {
    if (!errorMessage) return null;

    const errorComponent = <AlertsComponent errorMessage={errorMessage} />;

    if (challengerEnabled) {
      return <div className={styles.errorContainer}>{errorComponent}</div>;
    }

    return errorComponent;
  };

  const renderBasketButton = () => {
    const button = (
      <Button
        id="stickyAddToBasketButton"
        onClick={() => handleAddToBasket('Sticky add to basket')}
        data-testid="sticky_add_to_basket_button"
        disabled={disabled}
        size="medium"
        data-transaction-name={challengerEnabled ? 'PersistentATB' : 'NonPersistentATB'}
      >
        {updateBasket ? 'Update basket' : 'Add to basket'}
      </Button>
    );

    if (challengerEnabled) {
      return <div className={styles.buttonContainer}>{button}</div>;
    }

    return button;
  };

  const toggleStickyAddToBasketVisibility = debounce(() => {
    if (!primaryAddToBasketButtonRef.current) return;

    const { height, y } = primaryAddToBasketButtonRef.current.getBoundingClientRect();
    const primaryButtonStillVisible = y + height > 0;

    if (primaryButtonStillVisible || lessThan100PxFromBottomOfPage()) {
      setContainerIsVisible(false);
    } else {
      setContainerIsVisible(true);
    }
  }, 50);

  useEffect(() => {
    if (challengerEnabled) {
      setContainerIsVisible(true);
    } else {
      toggleStickyAddToBasketVisibility();
      window.addEventListener('scroll', toggleStickyAddToBasketVisibility);
    }

    return () => {
      window.removeEventListener('scroll', toggleStickyAddToBasketVisibility);
    };
  }, [challengerEnabled, toggleStickyAddToBasketVisibility]);

  return (
    <div
      className={[styles.container, containerIsVisible ? styles.containerVisible : ''].filter(Boolean).join(' ')}
      data-testid="sticky_add_to_basket_container"
      // Prevent “flash then disappear” when CSS modules load late:
      // keep it fixed + off-canvas until client decides it's visible.
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 10,
        visibility: containerIsVisible ? 'visible' : 'hidden',
        transform: containerIsVisible ? 'translateY(0%)' : 'translateY(100%)',
      }}
    >
      {renderProductInfo()}
      {renderErrorMessage()}
      {renderBasketButton()}
    </div>
  );
}

export default StickyAddToBasketButton;
